import { useIsVisible } from '@folklore/hooks';
import omit from 'lodash-es/omit';

import useCollection from '../../hooks/useCollection';
import { useCollectionUrlGenerator } from '../../hooks/useUrlGenerator';
import { getCollectionBackground } from '../../lib/backgrounds';

import { useIsCapture } from '../../contexts/LayoutContext';
import CollectionSummary from '../summaries/CollectionSummary';
import SummaryRowBlock, { SummaryRowBlockProps } from './SummaryRowBlock';

interface CollectionRowBlockProps extends SummaryRowBlockProps {
    slug: string;
    query?: Record<string, unknown> | null;
    loading?: 'lazy' | null;
    className?: string;
    withBackground?: boolean;
}

function CollectionRowBlock({
    slug,
    query = null,
    loading = 'lazy',
    className,
    withBackground = false,
    disabled = false,
    ...props
}: CollectionRowBlockProps) {
    const collectionUrl = useCollectionUrlGenerator();
    const isCapture = useIsCapture();
    const isLazyLoading = loading === 'lazy' && !isCapture;
    const { ref, visible: isVisible } = useIsVisible({
        persist: true,
        disabled: !isLazyLoading,
    });
    const shouldLoad = !isLazyLoading || isVisible;
    const { collection = null } = useCollection(slug, {
        enabled: shouldLoad && !disabled,
    });

    const finalQuery = {
        collection: slug,
        ...query,
    };

    return (
        <SummaryRowBlock
            summary={collection !== null ? omit(collection, ['children']) : null}
            url={collectionUrl(collection || { slug })}
            summaryComponent={CollectionSummary}
            background={withBackground ? getCollectionBackground(collection) : null}
            query={finalQuery}
            disabled={collection === null}
            loading={loading}
            ref={ref}
            className={className}
            {...props}
        />
    );
}

export default CollectionRowBlock;
